import { useEffect, useState } from 'react';
import type { BalancaDoEnsaio } from '../api/triviais';
import { BlocoDaFicha, EscolhaDeBalanca, JanelaDeDescarte } from '../Ficha';
import type { EquipamentoDaLista } from '../Ficha';
import { numero, paraNumero, plural } from '../formato';
import {
  Aviso, CampoNumerico, EscolhaPorCodigo, Selo, TabelaDeLancamento, comoCodigo,
} from '../ui';
import type { ColunaDeLancamento, LinhaDeLancamento, Opcao } from '../ui';
import { LembreteDaEstufa, SecaDaLinha, WDaLinha, naEstufaDaFicha } from './NaEstufa';
import type { Estufa } from './NaEstufa';

/**
 * O quadro de cápsulas de um dia só — lançadas e novas na mesma tabela.
 *
 * Era o quadro das três fichas triviais até a onda 290. Desde então
 * umidade, LL e LP pesam em dois dias (`Triviais.tsx`: pesagem úmida e
 * pesagem seca, RN-235); este quadro continua servindo às cápsulas de
 * umidade da massa específica, que ainda se lançam inteiras de uma vez.
 * Saiu de `Triviais.tsx` sem mudar uma linha do comportamento.
 *
 * Desde a onda 294 (RN-241) a Estufa também é dela: com `estufa`, a massa
 * seca da linha nova é opcional — sem ela a cápsula vai para a estufa — e
 * a coluna seca da linha gravada mostra «na estufa» e pesa ali mesmo.
 */

const CASAS = { umidade: 1, wCapsula: 2, limite: 0 } as const;
const num = (v: unknown, casas: number) => numero(v, casas);

/** Uma linha do quadro, ainda como texto — a conversão é na hora de gravar. */
interface LinhaCapsula {
  capsulaEquipamentoId: string;
  umido: string;
  seco: string;
  golpes: string;
}

const linhaVazia = (): LinhaCapsula =>
  ({ capsulaEquipamentoId: '', umido: '', seco: '', golpes: '' });

// ══ o quadro de cápsulas ══════════════════════════════════════════════

export interface QuadroProps {
  titulo: string;
  sub: string;
  rotuloLinha: string;
  comGolpes: boolean;
  linhas: any[];
  opcoesCapsula: Opcao[];
  equipamentos: EquipamentoDaLista[];
  /** A balança já gravada para esta bateria. */
  balanca: BalancaDoEnsaio | null;
  salvando: string | null;
  /** Grava as linhas novas e, junto, a balança da bateria. */
  aoGravar(corpo: { balancaEquipamentoId?: string; linhas: any[] }): Promise<boolean>;
  aoDescartar(linhaId: string, motivo: string): Promise<boolean>;
  /** Umidades que a depuração dos 5 % deixou de fora (só no LP). */
  foraDaMedia?: (linha: any) => boolean;
  /**
   * Quantas linhas o método exige, no mínimo (0181: três na umidade).
   * O quadro abre com as linhas que faltam para chegar lá, em vez de
   * uma só — quem senta para lançar já vê quantas cápsulas precisa.
   */
  minimo?: number;
  erroBalanca?: string;
  /** A Estufa do ensaio (onda 294): a seca pode ficar para depois. */
  estufa?: Estufa;
  congelado?: boolean;
  /** Recarregar a ficha depois de uma pesagem seca ou correção. */
  aoMudou?(): unknown;
}

export function QuadroCapsulas({
  titulo, sub, rotuloLinha, comGolpes, linhas, opcoesCapsula, equipamentos,
  balanca, salvando, aoGravar, aoDescartar, foraDaMedia, minimo, erroBalanca,
  estufa, congelado, aoMudou,
}: QuadroProps) {
  /* Com a Estufa, a seca não é obrigatória na linha nova (RN-241). */
  const secaOpcional = estufa !== undefined;
  const validas = linhas.filter((l: any) => !(l.descartada || l.descartado)).length;
  const faltam = Math.max(1, (minimo ?? 1) - validas);
  const [novas, setNovas] = useState<LinhaCapsula[]>(
    () => Array.from({ length: faltam }, () => linhaVazia()));
  const [colando, setColando] = useState(false);
  const [colagem, setColagem] = useState('');
  const [descartando, setDescartando] = useState<any | null>(null);
  /*
   * A balança é UMA para a bateria — «para triplicatas de umidade deve
   * escolher apenas 1 vez a balança (pois obrigatoriamente será a mesma
   * balança)», Humberto, 02/10/2026. Começa na que está gravada.
   */
  const [balancaId, setBalancaId] = useState(balanca?.id ?? '');
  useEffect(() => { setBalancaId(balanca?.id ?? ''); }, [balanca?.id]);
  const balancaMudou = balancaId !== '' && balancaId !== (balanca?.id ?? '');

  const mexer = (i: number, campo: keyof LinhaCapsula, v: string) =>
    setNovas((ls) => ls.map((l, j) => (j === i ? { ...l, [campo]: v } : l)));

  /**
   * A colagem: uma linha por cápsula.
   *
   * `cápsula<sep>úmido<sep>seco[<sep>golpes]`, com tabulação, ponto e
   * vírgula ou barra vertical como separador. A vírgula NÃO separa colunas
   * — em pt-BR ela é a decimal, e tratá-la como separador transformaria
   * "22,30" em duas colunas.
   */
  const aplicarColagem = () => {
    const linhasTxt = colagem.split(/\r?\n/).map((s) => s.trim()).filter(Boolean);
    const lidas: LinhaCapsula[] = [];
    for (const t of linhasTxt) {
      const p = t.split(/[\t;|]+/).map((s) => s.trim());
      if (p.length < 3) continue;
      const cap = opcoesCapsula.find(
        (o) => o.texto.toLowerCase() === p[0].toLowerCase());
      lidas.push({
        capsulaEquipamentoId: cap?.valor ?? '',
        umido: p[1], seco: p[2], golpes: p[3] ?? '',
      });
    }
    if (lidas.length) { setNovas(lidas); setColagem(''); setColando(false); }
  };

  /**
   * O que falta para «Gravar» poder gravar.
   *
   * Achado A3 da auditoria de 31/08: o botão ficava habilitado com o
   * quadro vazio, a pessoa tocava, e não acontecia NADA — nem gravação,
   * nem recusa, nem aviso. «Não acontecer nada» é o pior retorno
   * possível, porque a conclusão de quem está na bancada é que o sistema
   * travou, e o gesto seguinte é tocar de novo.
   *
   * Agora o botão diz o que espera, e continua alcançável: `aria-disabled`
   * em vez de `disabled`, para o motivo chegar ao mouse e ao teclado
   * (regra 160.10).
   */
  const prontas = novas.filter(
    (l) => l.capsulaEquipamentoId && l.umido && (secaOpcional || l.seco)
      && (!comGolpes || l.golpes));

  const oQueFalta = (): string | null => {
    if (prontas.length && !balancaId) return 'Falta escolher a balança.';
    if (prontas.length || balancaMudou) return null;
    const meio = novas.some(
      (l) => l.capsulaEquipamentoId || l.umido || l.seco || l.golpes);
    if (!meio) {
      if (secaOpcional) return 'Preencha a cápsula e a massa úmida para gravar.';
      return comGolpes
        ? 'Preencha a cápsula, os golpes e as duas massas para gravar.'
        : 'Preencha a cápsula e as duas massas para gravar.';
    }
    const sem = novas.find(
      (l) => !(l.capsulaEquipamentoId && l.umido && (secaOpcional || l.seco)
        && (!comGolpes || l.golpes)));
    if (!sem) return null;
    if (!sem.capsulaEquipamentoId) return 'Falta escolher a cápsula.';
    if (comGolpes && !sem.golpes) return 'Faltam os golpes.';
    if (!sem.umido) return 'Falta a massa de cápsula + solo úmido.';
    return 'Falta a massa de cápsula + solo seco.';
  };

  const gravar = async () => {
    const base = linhas.reduce((m: number, l: any) => Math.max(m, l.ordem), 0);
    const ok = await aoGravar({
      ...(balancaId ? { balancaEquipamentoId: balancaId } : {}),
      linhas: prontas.map((l, i) => ({
        ordem: base + i + 1,
        capsulaEquipamentoId: l.capsulaEquipamentoId,
        massaCapsulaSoloUmido: paraNumero(l.umido),
        ...(paraNumero(l.seco) != null || !secaOpcional
          ? { massaCapsulaSoloSeco: paraNumero(l.seco) } : {}),
        ...(comGolpes ? { golpes: Number(paraNumero(l.golpes) ?? 0) } : {}),
      })),
    });
    if (ok) {
      const restam = Math.max(1, (minimo ?? 1) - validas - prontas.length);
      setNovas(Array.from({ length: restam }, () => linhaVazia()));
    }
  };

  /*
   * Uma tabela só: as linhas lançadas em cima, as novas embaixo, com as
   * mesmas colunas — é a folha de bancada, e é assim que a pesagem
   * trocada salta aos olhos (70). No celular cada linha vira cartão, e a
   * ordem e a cápsula viram o TÍTULO dele, a umidade o RESULTADO.
   */
  const colunas: ColunaDeLancamento[] = [
    { texto: rotuloLinha === 'Ponto' ? 'Ponto' : 'Nº' },
    { texto: 'Cápsula' },
    ...(comGolpes ? [{ texto: 'Golpes', num: true }] : []),
    { texto: 'Tara (g)', num: true },
    { texto: 'Cáps. + solo úmido (g)', num: true },
    { texto: 'Cáps. + solo seco (g)', num: true },
    { texto: 'w (%)', num: true },
    { texto: '' },
  ];
  const iTara = comGolpes ? 3 : 2;
  const iUmidade = comGolpes ? 6 : 5;

  const linhasLancadas: LinhaDeLancamento[] = linhas.map((l) => {
    const morta = l.descartada || l.descartado;
    const fora = foraDaMedia?.(l) ?? false;
    const golpes = comGolpes ? [(
      <>
        {l.golpes}
        {l.fora_da_faixa_normativa && !morta
          ? <Selo tom="atencao">Fora de 15–35</Selo> : null}
      </>
    )] : [];
    return {
      chave: l.id,
      classe: morta ? 'descartada' : fora ? 'depurada' : undefined,
      titulo: (
        <>
          {rotuloLinha} {l.ordem} ·{' '}
          <span className="cod">{l.capsula ?? '—'}</span>
        </>
      ),
      resultado: morta ? '—' : secaOpcional && naEstufaDaFicha(l) ? 'na estufa' : (
        <>
          <span className="n">{num(l.umidade, CASAS.wCapsula)} %</span>
          {fora ? <Selo tom="atencao">Fora dos 5 %</Selo> : null}
        </>
      ),
      nota: morta ? `Descartada: ${l.motivo_descarte ?? ''}` : undefined,
      semCartao: [0, 1, iUmidade],
      celulas: [
        l.ordem,
        <span className="cod">{l.capsula}</span>,
        ...golpes,
        l.tara == null
          ? <Selo tom="critico">Sem calibração interna</Selo> : num(l.tara, 2),
        num(l.massa_capsula_solo_umido, 2),
        estufa
          ? <SecaDaLinha linha={l} estufa={estufa} congelado={congelado}
              opcoesCapsula={opcoesCapsula} aoMudou={() => aoMudou?.()} />
          : num(l.massa_capsula_solo_seco, 2),
        <b className="fe-w">
          {morta ? '—' : estufa ? <WDaLinha linha={l} casas={CASAS.wCapsula} />
            : num(l.umidade, CASAS.wCapsula)}
          {fora ? <Selo tom="atencao">Fora dos 5 %</Selo> : null}
        </b>,
        morta
          ? <span className="fe-motivo" title={l.motivo_descarte}>
              <Selo tom="neutro">Descartada</Selo>
            </span>
          : (
            <button type="button" className="botao discreto pequeno fe-descartar"
              aria-label={`Descartar ${rotuloLinha.toLowerCase()} ${l.ordem}`}
              disabled={salvando !== null}
              onClick={() => setDescartando(l)}>
              Descartar
            </button>
          ),
      ],
    };
  });

  const base = linhas.reduce((m: number, l: any) => Math.max(m, l.ordem), 0);
  const linhasNovas: LinhaDeLancamento[] = novas.map((l, i) => ({
    chave: `nova-${i}`,
    classe: 'nova',
    titulo: `${rotuloLinha} ${base + i + 1} · nova`,
    semCartao: [0, iTara, iUmidade],
    celulas: [
      <span className="sub">{base + i + 1}</span>,
      /*
       * Digitando o número, e não caçando na lista.
       *
       * «Gostaria de uma opção de digitar a numeração de cada um com
       *  dropdown para facilitar» (Testesolo, 01/09/2026). Com quarenta
       * cápsulas, a lista suspensa é uma caçada; com a cápsula 118 na
       * mão, «118» é um gesto.
       */
      <EscolhaPorCodigo
        rotulo=""
        opcoes={opcoesCapsula.map(comoCodigo)}
        valor={l.capsulaEquipamentoId}
        aoEscolher={(v) => mexer(i, 'capsulaEquipamentoId', v)}
      />,
      ...(comGolpes ? [(
        <CampoNumerico
          rotulo="Golpes" rotuloOculto inteiro
          value={l.golpes}
          onChange={(e) => mexer(i, 'golpes', e.target.value)}
        />
      )] : []),
      <span className="sub">—</span>,
      <CampoNumerico
        rotulo="Cápsula + solo úmido (g)" rotuloOculto
        value={l.umido}
        onChange={(e) => mexer(i, 'umido', e.target.value)}
      />,
      <CampoNumerico
        rotulo="Cápsula + solo seco (g)" rotuloOculto
        value={l.seco}
        onChange={(e) => mexer(i, 'seco', e.target.value)}
      />,
      <span className="sub">—</span>,
      /*
       * «Remover» sai da ordem de tabulação (achado de gravidade média).
       * Ele ficava DEPOIS do último número da linha: um Enter distraído
       * no fim da digitação apagava a linha inteira que acabara de ser
       * digitada. Continua clicável, e alcançável por quem navega por
       * teclado usando as setas dentro da linha.
       */
      <button type="button" className="remover" tabIndex={-1}
        aria-label={`Remover ${rotuloLinha.toLowerCase()} ${base + i + 1}`}
        title="Remover esta linha (ainda não gravada)"
        onClick={() => setNovas((ls) => ls.filter((_, j) => j !== i))}>
        ×
      </button>,
    ],
  }));

  const falta = oQueFalta();
  const plural2 = rotuloLinha === 'Ponto' ? 'Pontos' : 'Determinações';

  return (
    <BlocoDaFicha
      titulo={titulo}
      selo={validas > 0
        ? <Selo tom={validas >= (minimo ?? 1) ? 'ok' : 'atencao'}>
            {rotuloLinha === 'Ponto'
              ? plural(validas, 'válido', 'válidos')
              : plural(validas, 'válida', 'válidas')}
          </Selo>
        : undefined}
      sub={sub}>
      <div className="fe-miolo fe-campos">
        <EscolhaDeBalanca
          equipamentos={equipamentos}
          valor={balancaId}
          aoEscolher={setBalancaId}
          erro={erroBalanca}
        />
      </div>

      <div className="fe-tabela">
        <div className="fe-tabela-topo">
          <h3>{plural2} <small>{linhas.length
            ? `${linhas.length} ${rotuloLinha === 'Ponto' ? 'lançados' : 'lançadas'}`
            : 'nenhuma lançada ainda'}</small></h3>
          <button type="button" className="botao discreto pequeno"
            aria-expanded={colando}
            onClick={() => setColando((v) => !v)}>
            Colar da planilha
          </button>
          <button type="button" className="botao discreto pequeno"
            onClick={() => setNovas((ls) => [...ls, linhaVazia()])}>
            + linha
          </button>
        </div>

        {colando && (
          <div className="colagem">
            <textarea
              rows={3}
              aria-label="Colar da planilha"
              placeholder={`Uma linha por cápsula:\nCAP-07${
                '\t'}22,30${'\t'}20,00${comGolpes ? `${'\t'}25` : ''}`}
              value={colagem}
              onChange={(e) => setColagem(e.target.value)}
            />
            <button type="button" className="botao secundario"
              onClick={aplicarColagem} disabled={!colagem.trim()}>
              Ler a colagem
            </button>
          </div>
        )}

        {/*
          * `lancamento`, e não `entrada`: `entrada` é a classe da TELA DE
          * LOGIN, e posta aqui esticava a planilha até a altura da janela
          * (varredura `109.1`, 01/09/2026).
          */}
        <TabelaDeLancamento
          rotulo={`${plural2} — lançadas e novas`}
          classe="capsulas fe-lanc"
          colunas={colunas}
          linhas={[...linhasLancadas, ...linhasNovas]}
        />
        {estufa && !congelado && (
          <LembreteDaEstufa quantas={linhas.filter(naEstufaDaFicha).length} />
        )}

        <div className="acoes acoes-fixas fe-acoes">
          {falta
            ? <p className="o-que-falta">{falta}</p>
            : <p className="fe-lembrete">Linha em branco não sobe; a errada se descarta com motivo.</p>}
          <button type="button" className="principal" data-gravar-tabela=""
            aria-disabled={falta !== null || salvando !== null}
            title={falta ?? undefined}
            onClick={() => { if (!falta && salvando === null) void gravar(); }}>
            {salvando === 'linhas' ? 'Gravando…' : `Gravar ${plural2.toLowerCase()}`}
          </button>
        </div>
      </div>

      {opcoesCapsula.length === 0 && (
        <Aviso tipo="atencao">
          Não há cápsula com calibração interna cadastrada. Sem a tara calibrada o
          sistema não calcula umidade — e não vai inventar uma.
        </Aviso>
      )}

      <JanelaDeDescarte
        alvo={descartando
          ? `${rotuloLinha.toLowerCase()} ${descartando.ordem} · ${descartando.capsula ?? ''}`
          : null}
        aoFechar={() => setDescartando(null)}
        aoConfirmar={(motivo) => aoDescartar(descartando.id, motivo)}
        salvando={salvando !== null}
      />
    </BlocoDaFicha>
  );
}

